// Home — executive structural market radar (WP12 · Commit-6).
// Rendered EXCLUSIVELY from the active authoritative WP11 package
// (/api/wp12/home + /api/wp12/market). Home uses the published medium-term
// 12-week structural view — there is no global horizon recalculation switch.
// Geography selection filters object VISIBILITY only — no score or trend is
// ever recalculated client-side.
import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useWp12 } from "./useWp12";
import InfoTip from "./InfoTip";
import { WorldPresenceMap } from "./WorldPresenceMap";
import { RegionPanel } from "./RegionPanel";
import { LeadersChart } from "./LeadersChart";
import { TopPatternsTable } from "./TopPatternsTable";
import { ZoneCards } from "./ZoneCards";

const HOME_HORIZON_WEEKS = 12; // published medium-term structural view

const SectionHead = ({ title, subtitle, right }) => (
    <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
            <h2 className="text-base md:text-lg font-semibold">{title}</h2>
            {subtitle && <p className="text-[11px] text-[hsl(var(--mute))] mt-0.5 max-w-2xl">{subtitle}</p>}
        </div>
        {right}
    </div>
);

const Home = () => {
    const homeRes = useWp12("home");
    const marketRes = useWp12("market");
    const { isAdmin } = useAuth();
    const home = homeRes.data;
    const market = marketRes.data;
    const [selectedGeography, setSelectedGeography] = useState(null);

    const zonesByKey = useMemo(() => {
        const map = {};
        for (const z of market?.marketZones || []) map[z.scopeKey] = z;
        return map;
    }, [market]);

    const cardsByZone = useMemo(() => {
        const map = {};
        for (const c of market?.patternCards || []) (map[c.marketZoneKey] = map[c.marketZoneKey] || []).push(c);
        return map;
    }, [market]);

    // Canonical published use-case labels (patternCards join) for consistent display
    const useCaseLabelByKey = useMemo(() => {
        const map = {};
        for (const c of market?.patternCards || []) {
            if (c.useCaseKey && c.useCaseLabel && !map[c.useCaseKey]) map[c.useCaseKey] = c.useCaseLabel;
        }
        return map;
    }, [market]);

    const selectedRegion = useMemo(
        () => (home?.geographyRadar || []).find((r) => r.geoKey === selectedGeography) || null,
        [home, selectedGeography]
    );

    // Region → Market Zone via published geographyRadar[].marketZoneRefs (visibility filter only)
    const geoZoneKeys = useMemo(() => {
        if (!selectedRegion) return null;
        return new Set(selectedRegion.marketZoneRefs || []);
    }, [selectedRegion]);

    // Region → Pattern via published geographyRadar[].patternRefs (Commit-6 hydrated).
    // Membership/visibility only — the pattern score stays the global PATTERN-scoped score.
    const patternGeoFilterAvailable = (selectedRegion?.patternRefs || []).length > 0;

    const visibleLeaders = useMemo(() => {
        const leaders = home?.crossZoneLeaders || [];
        if (!geoZoneKeys) return leaders;
        return leaders.filter((l) => geoZoneKeys.has(l.marketZoneKey));
    }, [home, geoZoneKeys]);

    const visiblePatternCards = useMemo(() => {
        const cards = market?.patternCards || [];
        if (!selectedGeography || !patternGeoFilterAvailable) return cards;
        const refs = new Set(selectedRegion.patternRefs);
        return cards.filter((c) => refs.has(c.canonicalPatternId));
    }, [market, selectedGeography, patternGeoFilterAvailable, selectedRegion]);

    const orderedZones = useMemo(() => {
        const order = (home?.marketZoneSummaries || []).slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)).map((z) => z.marketZoneKey);
        const zones = market?.marketZones || [];
        return order.length ? order.map((k) => zonesByKey[k]).filter(Boolean) : zones;
    }, [home, market, zonesByKey]);

    if (homeRes.status === "loading" || marketRes.status === "loading") {
        return <div className="p-8 text-sm text-[hsl(var(--mute))]" data-testid="wp12-home-loading">Loading the active publication package…</div>;
    }
    if (!home || !market) {
        return (
            <div className="p-8" data-testid="wp12-home-error">
                <p className="text-sm text-[hsl(var(--warning-ink))]">The active publication package is unavailable{homeRes.error ? ` — ${homeRes.error}` : ""}.</p>
            </div>
        );
    }

    const geographyLabel = selectedRegion?.label || "";
    const cutoffLabel = (() => {
        const [y, mo, d] = String(home.dataCutoff || "").split("-");
        const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        return mo ? `${+d} ${months[+mo - 1]} ${y}` : home.dataCutoff;
    })();
    const FilterChip = () =>
        selectedGeography ? (
            <button
                type="button"
                data-testid="wp12-home-geo-chip"
                onClick={() => setSelectedGeography(null)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))]/50 text-[11px] font-medium hover:bg-[hsl(var(--accent-soft))]"
            >
                {geographyLabel} <X size={11} />
            </button>
        ) : null;

    return (
        <div className="space-y-8" data-testid="v25-home">
            {/* ── 1. Interactive world map / geography filter ────────────────── */}
            <section className="space-y-3" data-testid="wp12-home-geography">
                <SectionHead
                    title={
                        <span className="inline-flex items-center gap-2">
                            Where structural activity is concentrated
                            <InfoTip label="Geography methodology" testId="wp12-home-geo-methodology-tip">
                                Map intensity shows the published regional share of observed structural
                                activity. Geography carries no analytical score, and region selection
                                filters visibility only — no value is recalculated.
                            </InfoTip>
                        </span>
                    }
                    subtitle="Click a region to inspect it and filter the sections below."
                    right={
                        <span className="num text-[10px] text-[hsl(var(--mute))]" data-testid="wp12-home-reporting-week">
                            {home.asOfWeek} · cutoff {cutoffLabel}
                        </span>
                    }
                />
                <WorldPresenceMap regions={home.geographyRadar || []} selectedGeoKey={selectedGeography} onSelect={setSelectedGeography} />
                {selectedRegion && (
                    <RegionPanel
                        region={selectedRegion}
                        zonesByKey={zonesByKey}
                        useCaseLabelByKey={useCaseLabelByKey}
                        onClose={() => setSelectedGeography(null)}
                    />
                )}
            </section>

            {/* ── 2. Cross-zone leading patterns chart ───────────────────────── */}
            <section className="space-y-4" data-testid="wp12-home-leaders">
                <SectionHead
                    title={
                        <span className="inline-flex items-center gap-2">
                            {selectedGeography ? `Global leading patterns in Market Zones active in ${geographyLabel}` : "Leading pattern per market zone"}
                            <InfoTip label="Chart methodology" testId="wp12-home-leaders-semantics-tip">
                                The publisher selects the strongest pattern in each Market Zone by highest
                                current pattern score, over the full published 12-week timeline. Leader
                                selection and scores are not recalculated by region. When a region is
                                selected, these may differ from the table below because the chart keeps
                                the published global leader for each active Market Zone, while the table
                                contains Patterns directly associated with the selected region.
                            </InfoTip>
                        </span>
                    }
                    subtitle={selectedGeography
                        ? `Published global leader for each Market Zone represented in ${geographyLabel}.`
                        : "Strongest published pattern in each Market Zone · 12-week score history."}
                    right={<FilterChip />}
                />
                <LeadersChart leaders={visibleLeaders} horizonWeeks={HOME_HORIZON_WEEKS} />
            </section>

            {/* ── 3. Top structural patterns ─────────────────────────────────── */}
            <section className="space-y-4" data-testid="wp12-home-patterns">
                <TopPatternsTable
                    patternCards={visiblePatternCards}
                    zonesByKey={zonesByKey}
                    selectedGeography={selectedGeography}
                    geographyLabel={geographyLabel}
                    patternGeoFilterAvailable={patternGeoFilterAvailable}
                />
            </section>

            {/* ── 5. Navigate the market — six rich zone cards (always visible) */}
            <section className="space-y-4" data-testid="wp12-home-zones">
                <SectionHead
                    title="Navigate the market"
                    subtitle="Six Market Zones — published structural state, footprint and the two strongest Patterns. Drill in for Use Cases, Patterns and Evidence."
                />
                <ZoneCards zones={orderedZones} cardsByZone={cardsByZone} horizonWeeks={HOME_HORIZON_WEEKS} highlightedZoneKeys={geoZoneKeys} />
            </section>

            {/* Package identity — admin/debug only */}
            {isAdmin && (
                <div className="text-[9px] text-[hsl(var(--mute))] num" data-testid="wp12-home-package-id" data-package-id={homeRes.packageId} title={homeRes.packageId}>Active publication verified</div>
            )}
        </div>
    );
};

export default Home;
